import React from 'react';
import { theme } from '../../styles/theme';
import { RunState } from '../../types/run';
import { SpinningGlobe } from '../ui/SpinningGlobe';

interface MainMenuProps {
  onNewRun: () => void;
  onContinueRun: () => void;
  onPractice: () => void;
  onOpenCards: () => void;
  onOpenPassives: () => void;
  onOpenAgents: () => void;
  onOpenStyleguide: () => void;
  onOpenTutorial: () => void;
  savedRun: RunState | null;
}

export function MainMenu({
  onNewRun,
  onContinueRun,
  onPractice,
  onOpenCards,
  onOpenPassives,
  onOpenAgents,
  onOpenStyleguide,
  onOpenTutorial,
  savedRun,
}: MainMenuProps) {
  return (
    <div style={styles.container}>
      {/* Ambient background globe — wireframe world slowly rotating behind
          the menu content. Positioned absolute, sized relative to the
          viewport min-dimension so it scales on smaller displays, and
          kept at a low opacity so menu text stays readable. pointerEvents
          is handled inside the component (aria-hidden canvas). */}
      <div style={styles.globeWrapper} aria-hidden>
        <SpinningGlobe
          size={720}
          color="#44ccff"
          rotationSpeed={0.12}
          pointCount={520}
          neighbors={4}
          style={{ opacity: 0.28 }}
        />
      </div>
      <div style={styles.inner}>
        {/* Classification header */}
        <div style={styles.classification}>TOP SECRET // NOFORN</div>

        {/* Title */}
        <h1 style={styles.title}>GEO-SLEEPER</h1>

        <div style={styles.divider} />

        {/* Buttons */}
        <div style={styles.buttonGroup}>
          <button
            style={styles.primaryButton}
            onClick={onNewRun}
            onMouseEnter={e => {
              (e.target as HTMLElement).style.background = 'rgba(68, 204, 136, 0.15)';
              (e.target as HTMLElement).style.borderColor = '#44cc88';
            }}
            onMouseLeave={e => {
              (e.target as HTMLElement).style.background = 'rgba(68, 204, 136, 0.06)';
              (e.target as HTMLElement).style.borderColor = 'rgba(68, 204, 136, 0.5)';
            }}
          >
            NEW RUN
          </button>

          {savedRun && (
            <button
              style={styles.continueButton}
              onClick={onContinueRun}
              onMouseEnter={e => {
                (e.target as HTMLElement).style.background = 'rgba(68, 170, 255, 0.15)';
                (e.target as HTMLElement).style.borderColor = '#44aaff';
              }}
              onMouseLeave={e => {
                (e.target as HTMLElement).style.background = 'rgba(68, 170, 255, 0.06)';
                (e.target as HTMLElement).style.borderColor = 'rgba(68, 170, 255, 0.5)';
              }}
            >
              <div>CONTINUE RUN</div>
              <div style={styles.continueDetail}>
                Round {savedRun.round} — {savedRun.cityHistory.length > 0
                  ? savedRun.cityHistory[savedRun.cityHistory.length - 1].toUpperCase()
                  : 'DEPLOYING'}
              </div>
            </button>
          )}

          <div style={styles.spacer} />

          <button
            style={styles.practiceButton}
            onClick={onPractice}
            onMouseEnter={e => {
              (e.target as HTMLElement).style.background = 'rgba(255, 255, 255, 0.06)';
            }}
            onMouseLeave={e => {
              (e.target as HTMLElement).style.background = 'none';
            }}
          >
            PRACTICE
          </button>

          <button
            style={styles.practiceButton}
            onClick={onOpenTutorial}
            onMouseEnter={e => {
              (e.target as HTMLElement).style.background = 'rgba(255, 255, 255, 0.06)';
            }}
            onMouseLeave={e => {
              (e.target as HTMLElement).style.background = 'none';
            }}
          >
            TUTORIAL
          </button>

          {/* Reference pages — smaller row for browsing the game's
              implemented cards/passives and auditing the UI landscape. */}
          <div style={styles.referenceRow}>
            <button
              style={styles.referenceButton}
              onClick={onOpenCards}
              onMouseEnter={e => { (e.target as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={e => { (e.target as HTMLElement).style.background = 'none'; }}
            >
              CARDS
            </button>
            <button
              style={styles.referenceButton}
              onClick={onOpenPassives}
              onMouseEnter={e => { (e.target as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={e => { (e.target as HTMLElement).style.background = 'none'; }}
            >
              PASSIVES
            </button>
            <button
              style={styles.referenceButton}
              onClick={onOpenAgents}
              onMouseEnter={e => { (e.target as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={e => { (e.target as HTMLElement).style.background = 'none'; }}
            >
              AGENTS
            </button>
            <button
              style={styles.referenceButton}
              onClick={onOpenStyleguide}
              onMouseEnter={e => { (e.target as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={e => { (e.target as HTMLElement).style.background = 'none'; }}
            >
              STYLEGUIDE
            </button>
          </div>
        </div>

        <div style={styles.footer}>
          <div style={styles.footerLine}>CLASSIFICATION: TS/SCI</div>
          <div style={styles.footerLine}>HANDLING: NOFORN / ORCON</div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: theme.fonts.mono,
    overflow: 'hidden',
  },
  // Full-viewport wrapper that centers the globe canvas behind the menu
  // content. pointerEvents:none so clicks pass through to the buttons.
  globeWrapper: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    zIndex: 0,
  },
  inner: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '460px',
    width: '100%',
    padding: '48px 32px',
  },
  classification: {
    fontSize: '10px',
    letterSpacing: '4px',
    color: '#ff5050',
    fontWeight: 700,
    marginBottom: '12px',
  },
  title: {
    fontSize: '40px',
    fontWeight: 700,
    letterSpacing: '10px',
    color: theme.colors.textPrimary,
    margin: 0,
    textAlign: 'center',
  },
  divider: {
    width: '100%',
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${theme.colors.border}, transparent)`,
    margin: '32px 0',
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    maxWidth: '320px',
    alignItems: 'stretch',
  },
  primaryButton: {
    background: 'rgba(68, 204, 136, 0.06)',
    border: '1px solid rgba(68, 204, 136, 0.5)',
    color: '#44cc88',
    padding: '16px 24px',
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '4px',
    cursor: 'pointer',
    fontFamily: theme.fonts.mono,
    borderRadius: '4px',
    transition: 'all 0.15s',
  },
  continueButton: {
    background: 'rgba(68, 170, 255, 0.06)',
    border: '1px solid rgba(68, 170, 255, 0.5)',
    color: '#44aaff',
    padding: '14px 24px',
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '3px',
    cursor: 'pointer',
    fontFamily: theme.fonts.mono,
    borderRadius: '4px',
    transition: 'all 0.15s',
    textAlign: 'center',
  },
  continueDetail: {
    fontSize: '10px',
    letterSpacing: '1px',
    color: theme.colors.textSecondary,
    marginTop: '4px',
    fontWeight: 400,
  },
  spacer: {
    height: '12px',
  },
  practiceButton: {
    background: 'none',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textSecondary,
    padding: '10px 24px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '3px',
    cursor: 'pointer',
    fontFamily: theme.fonts.mono,
    borderRadius: '4px',
    transition: 'all 0.15s',
  },
  referenceRow: {
    display: 'flex',
    gap: '6px',
    marginTop: '16px',
  },
  referenceButton: {
    flex: 1,
    background: 'none',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textDim,
    padding: '8px 10px',
    fontSize: '9px',
    fontWeight: 600,
    letterSpacing: '2px',
    cursor: 'pointer',
    fontFamily: theme.fonts.mono,
    borderRadius: '4px',
    transition: 'all 0.15s',
  },
  footer: {
    marginTop: '48px',
    textAlign: 'center',
    opacity: 0.3,
  },
  footerLine: {
    fontSize: '8px',
    letterSpacing: '2px',
    color: theme.colors.textSecondary,
    lineHeight: '1.8',
  },
};
